/**
 * input-limits.ts
 *
 * Shared input length guards. Prevents giant pastes (e.g. 50,000-char strings)
 * from triggering re-render loops, regex backtracking, or browser hangs in
 * any account / lead / wizard form.
 *
 * Limits are intentionally generous but bounded.
 */

export const INPUT_LIMITS = {
  /** Short identifier text — names, titles, single-line labels */
  name:        200,
  /** RFC 5321 max for an email address */
  email:       254,
  /** Phone number — well above international + extension formats */
  phone:       30,
  /** Long-form free text — notes, descriptions, evidence */
  description: 5000,
  /** URL — practical browser cap */
  url:         2000,
  /** Generic short field fallback */
  short:       200,
} as const;

export type InputLimitKey = keyof typeof INPUT_LIMITS;

/**
 * Safely truncate a string to `maxLength` code units.
 * Wrapped in try/catch so that pathological inputs (e.g. detached surrogates,
 * proxy strings) never throw out of the React event handler.
 *
 * Note: we slice by code unit, not grapheme — emoji clusters may be cut at
 * the boundary, but that's preferable to crashing.
 */
export function safeTruncate(value: unknown, maxLength = INPUT_LIMITS.short): string {
  try {
    if (value == null) return "";
    const s = typeof value === "string" ? value : String(value);
    if (s.length <= maxLength) return s;
    return s.slice(0, maxLength);
  } catch {
    return "";
  }
}

/**
 * Build an onChange handler that truncates before calling the setter.
 * Drop-in replacement for `(e) => setX(e.target.value)`.
 */
export function makeBoundedOnChange<T extends HTMLInputElement | HTMLTextAreaElement>(
  setter: (v: string) => void,
  maxLength: number = INPUT_LIMITS.short,
) {
  return (e: React.ChangeEvent<T>) => {
    setter(safeTruncate(e.target.value, maxLength));
  };
}

/**
 * Simple debounce helper for expensive per-keystroke validators.
 */
export function debounce<F extends (...args: any[]) => void>(fn: F, wait = 150): F {
  let t: ReturnType<typeof setTimeout> | undefined;
  return ((...args: any[]) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  }) as F;
}
